import { VorgangStatus } from '@comparit/common-api';
import type { BrandingService } from '@comparit/infrastructure';
import { EMPTY, catchError, exhaustMap, filter, take, timeout, timer } from 'rxjs';
import type { Observable } from 'rxjs';
import type { VorgangInput } from '../interfaces/vorgang-input.model';

export interface AntragNotice {
  headline: string;
  message: string;
  state: 'done' | 'busy';
}

/** A Vorgang whose Antrag is being sent is asked, one request at a time, for about two minutes. */
const CHECK_INTERVAL_MS = 3000;
const CHECK_ATTEMPTS = 40;

/** How a sent or still sending Antrag is shown instead of offering its tariff again; null while it is open. */
export function antragNotice(status: string): AntragNotice | null {
  switch (status) {
    case VorgangStatus.ContractEmailSent:
      return {
        headline: 'Antrag übermittelt',
        message:
          'Dein Antrag für diesen Tarif wurde übermittelt. Die Bestätigung haben wir dir per E-Mail geschickt.',
        state: 'done',
      };
    case VorgangStatus.ContractSent:
      return {
        headline: 'Antrag übermittelt',
        message: 'Dein Antrag für diesen Tarif wurde übermittelt.',
        state: 'done',
      };
    case VorgangStatus.ContractPending:
      return {
        headline: 'Antrag wird übermittelt',
        message:
          'Dein Antrag für diesen Tarif wird gerade übermittelt. Das kann einen Moment dauern.',
        state: 'busy',
      };
    default:
      return null;
  }
}

/** Whom the customer turns to, in the words of the brand. */
export function contactTerm(branding: BrandingService): string {
  return branding.config()?.features.setOrderHintUsesBerater
    ? 'deinen Berater'
    : 'deine/n Vermittler/in';
}

/** The Vorgang once its Antrag is no longer being sent; empty if it still is after about two minutes. */
export function settledVorgang(read: () => Observable<VorgangInput>): Observable<VorgangInput> {
  return timer(0, CHECK_INTERVAL_MS).pipe(
    take(CHECK_ATTEMPTS),
    // A read that fails or hangs is simply asked again with the next tick.
    exhaustMap(() =>
      read().pipe(
        timeout(CHECK_INTERVAL_MS),
        catchError(() => EMPTY)
      )
    ),
    filter(vorgang => vorgang.status !== VorgangStatus.ContractPending),
    take(1)
  );
}
